import type { Role } from "./domain";
import type { DocumentRegistryRow } from "./documentRegistryContract";

export const incomingReceiptsEndpoint = "/api/v1/procurement/incoming-documents" as const;
export type IncomingKind = "opening_receipt" | "purchase_order" | "physical_receiving";
export interface IncomingTarget { kind: IncomingKind; id: string }
const validId = (id: string) => /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(id);

export function incomingPurchaseReceiptFromHash(hash: string, role: Role): string | null {
  const query = new URLSearchParams(hash.split("?", 2)[1] || "");
  const id = query.get("receiptId");
  if (!id) return null;
  if (!validId(id) || ["openingStockId", "purchaseOrderId", "receivingId"].some(key => query.has(key))) throw new Error("Некорректная ссылка на приходную накладную");
  if (role !== "admin") throw new Error("Документ недоступен для этой роли");
  return id;
}

export function incomingKinds(role: Role): readonly IncomingKind[] {
  return role === "admin" ? ["purchase_order", "opening_receipt"]
    : role === "operator" ? ["physical_receiving", "opening_receipt"] : [];
}

export function incomingTargetFromHash(hash: string, role: Role): IncomingTarget | null {
  const query = new URLSearchParams(hash.split("?", 2)[1] || "");
  const keys: Array<[IncomingKind, string]> = [
    ["opening_receipt", "openingStockId"], ["purchase_order", "purchaseOrderId"], ["physical_receiving", "receivingId"],
  ];
  incomingPurchaseReceiptFromHash(hash, role);
  const targets = keys.flatMap(([kind, key]) => {
    const id = query.get(key);
    if (id && !validId(id)) throw new Error("Некорректный идентификатор приходной накладной");
    return id && incomingKinds(role).includes(kind) ? [{ kind, id }] : [];
  });
  if (targets.length > 1) throw new Error("В ссылке указано несколько приходных документов");
  return targets[0] || null;
}

export function incomingTargetFromRow(row: DocumentRegistryRow, role: Role): IncomingTarget {
  if (row.origin !== "native" || row.historicalReadOnly || !incomingKinds(role).includes(row.nativeKind as IncomingKind)) {
    throw new Error("Документ не относится к разрешённой приёмке");
  }
  const id = row.nativeId || row.id;
  if (!id || id !== row.id) throw new Error("Сервер не подтвердил идентификатор приходной накладной");
  return { kind: row.nativeKind as IncomingKind, id };
}

/** Old immutable notices keep their UUID and select the common receiving view. */
export function normalizeIncomingReceiptHash(hash: string): string {
  const [path, raw = ""] = hash.split("?", 2);
  if (path !== "#/purchases") return hash;
  const query = new URLSearchParams(raw);
  if (query.get("view") !== "opening") return hash;
  query.set("view", "receipts");
  return `${path}?${query.toString()}`;
}
